/**
 * Central icon module — replaces emoji literals scattered across the app
 * with MUI icon components. Components import named icons from here so the
 * mapping is centralized and consistent.
 *
 * Naming convention: each export is a *role-named* React component (e.g.
 * `CreatorIcon`, `VerifiedIcon`) rather than a generic icon name. This
 * preserves the *intent* of the original emoji and keeps semantics readable
 * at the call site.
 *
 * Colors: where the original emoji had a strong color signal (warning amber,
 * success green, gold sparkle, red heart) we apply a matching `htmlColor`.
 * For neutral usages we leave the color to the parent (`color="inherit"`).
 */
import React from 'react';
import { SvgIcon, type SvgIconProps } from '@mui/material';
import { ReactComponent as VellumProSparkleSvg } from '../assets/brand/vellum-pro-icon-flat.svg';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LockIcon from '@mui/icons-material/Lock';
import LinkIcon from '@mui/icons-material/Link';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import BadgeIcon from '@mui/icons-material/Badge';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BarChartIcon from '@mui/icons-material/BarChart';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PublicIcon from '@mui/icons-material/Public';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BlockIcon from '@mui/icons-material/Block';
import CelebrationIcon from '@mui/icons-material/Celebration';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import FolderIcon from '@mui/icons-material/Folder';
import GavelIcon from '@mui/icons-material/Gavel';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import TuneIcon from '@mui/icons-material/Tune';
import BoltIcon from '@mui/icons-material/Bolt';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import SettingsIcon from '@mui/icons-material/Settings';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

/**
 * Brand colors used for icon tinting. These match the design tokens used
 * elsewhere in the app — ideally these would come from the theme but a
 * hardcoded palette keeps icon usage simple at call sites.
 */
const COLOR = {
  gold: '#d4af37',
  green: '#2e7d32',
  red: '#c62828',
  amber: '#ed6c02',
  blue: '#1976d2',
  pink: '#d6336c',
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Brand / decorative
// ────────────────────────────────────────────────────────────────────────────

/**
 * ✦ — Vellum's brand sparkle, used next to "Pro" / "Author" tier labels and
 * other premium-feature markers throughout the app. Uses the official brand
 * asset (`vellum-pro-icon-flat.svg`) wrapped in an MUI `SvgIcon` so it sizes
 * and aligns like every other icon. Defaults to gold; override `htmlColor`
 * for placements that need a different tint.
 */
export const SparkleIcon = (props: SvgIconProps) => (
  <SvgIcon
    component={VellumProSparkleSvg}
    inheritViewBox
    htmlColor={COLOR.gold}
    fontSize="small"
    {...props}
  />
);

/**
 * ✨ — Sparkle for "new" indicators (e.g. "New users this period"). Distinct
 * from the brand `SparkleIcon` so we don't dilute the brand mark on generic
 * stat tiles. Uses MUI's `AutoAwesome` glyph in gold.
 */
export const NewSparkleIcon = (props: SvgIconProps) => (
  <AutoAwesomeIcon htmlColor={COLOR.gold} fontSize="small" {...props} />
);

// ────────────────────────────────────────────────────────────────────────────
// Status / verification
// ────────────────────────────────────────────────────────────────────────────

/** ✓ — neutral verified check (e.g. "✓ Verified"). */
export const VerifiedCheckIcon = (props: SvgIconProps) => (
  <CheckIcon htmlColor={COLOR.green} fontSize="small" {...props} />
);

/** ✅ — solid success check inside a circle. */
export const SuccessIcon = (props: SvgIconProps) => (
  <CheckCircleIcon htmlColor={COLOR.green} fontSize="small" {...props} />
);

/** ✕ — close button. */
export { CloseIcon };

/** ✗ — "not included" / "missing feature" cross. */
export const NotIncludedIcon = (props: SvgIconProps) => (
  <CancelIcon htmlColor={COLOR.red} fontSize="small" {...props} />
);

/** ⚠ ⚠️ — warning. */
export const WarningIcon = (props: SvgIconProps) => (
  <WarningAmberIcon htmlColor={COLOR.amber} fontSize="small" {...props} />
);

// ────────────────────────────────────────────────────────────────────────────
// Domain icons — content & creator
// ────────────────────────────────────────────────────────────────────────────

/** ✍ ✍️ — creator / write. */
export const CreatorIcon = EditNoteIcon;

/** ✏ ✏️ — edit. */
export { EditIcon };

/** 📖 — book / read / journal. */
export const BookIcon = MenuBookIcon;

/** 📚 — series, library. */
export const SeriesIcon = LibraryBooksIcon;

/** 📄 — document. */
export const DocumentIcon = DescriptionIcon;

/** 📜 — audit log / scroll. */
export const AuditLogIcon = HistoryEduIcon;

/** 📰 — press / media. */
export const PressIcon = NewspaperIcon;

/** 🔖 — bookmark. */
export { BookmarkIcon };

// ────────────────────────────────────────────────────────────────────────────
// Engagement / social
// ────────────────────────────────────────────────────────────────────────────

/** 👁 — views / visibility. */
export const ViewsIcon = VisibilityIcon;

/** ♥ — likes / favorites. */
export const LikeIcon = (props: SvgIconProps) => (
  <FavoriteIcon htmlColor={COLOR.pink} fontSize="small" {...props} />
);

/** 👥 — people / users. */
export const UsersIcon = PeopleIcon;

/** 👤 — single user. */
export const UserIcon = PersonIcon;

/** 🤝 — handshake / partnership / disclosure. */
export const TrustIcon = HandshakeIcon;

/** 🤖 — AI / robot. */
export const AiIcon = SmartToyIcon;

/** 🎉 — celebration. */
export { CelebrationIcon };

/** 😕 — disappointed / not found. */
export const SadIcon = SentimentDissatisfiedIcon;

// ────────────────────────────────────────────────────────────────────────────
// Money / commerce
// ────────────────────────────────────────────────────────────────────────────

/** 💰 — money / revenue. */
export const MoneyIcon = MonetizationOnIcon;

/** 💳 — credit card / payment. */
export { CreditCardIcon };

/** 🎁 — gift. */
export const GiftIcon = CardGiftcardIcon;

/** 🏦 — bank / payouts. */
export const BankIcon = AccountBalanceIcon;

/** 🧾 — receipt / tax document. */
export const ReceiptIcon = ReceiptLongIcon;

/** 🪪 — ID card / verification. */
export const IdCardIcon = BadgeIcon;

// ────────────────────────────────────────────────────────────────────────────
// Privacy / security
// ────────────────────────────────────────────────────────────────────────────

/** 🔒 — locked / private. */
export { LockIcon };

/** 🔐 — encrypted (lock + key). */
export const EncryptedIcon = EnhancedEncryptionIcon;

/** 🔗 — link / referrals. */
export { LinkIcon };

/** 🔍 — search. */
export { SearchIcon };

/** 🔔 — notifications. */
export { NotificationsIcon };

// ────────────────────────────────────────────────────────────────────────────
// Charts / trends
// ────────────────────────────────────────────────────────────────────────────

/** 📊 — analytics / bar chart. */
export const AnalyticsIcon = BarChartIcon;

/** 📈 — trending up. */
export const TrendingUpColorIcon = (props: SvgIconProps) => (
  <TrendingUpIcon htmlColor={COLOR.green} fontSize="small" {...props} />
);

/** 📉 — trending down. */
export const TrendingDownColorIcon = (props: SvgIconProps) => (
  <TrendingDownIcon htmlColor={COLOR.red} fontSize="small" {...props} />
);

// ────────────────────────────────────────────────────────────────────────────
// Moderation / admin
// ────────────────────────────────────────────────────────────────────────────

/** 🚩 ⚑ — flag / report. */
export { FlagIcon, OutlinedFlagIcon };

/** ⚖ — appeals / justice. */
export const AppealsIcon = GavelIcon;

/** 🚫 — banned / blocked. */
export const BannedIcon = (props: SvgIconProps) => (
  <BlockIcon htmlColor={COLOR.red} fontSize="small" {...props} />
);

/** 🗂 — folder / content moderation. */
export const ContentModerationIcon = FolderIcon;

/** 🎟 — ticket / early access. */
export const TicketIcon = ConfirmationNumberIcon;

/** 🗑 — trash / erasure. */
export const TrashIcon = DeleteIcon;

/** 📋 — clipboard / list / report. */
export const ClipboardIcon = AssignmentIcon;

// ────────────────────────────────────────────────────────────────────────────
// Misc
// ────────────────────────────────────────────────────────────────────────────

/** 🌐 — globe / domain / public. */
export const GlobeIcon = PublicIcon;

/** 💡 — tip / idea. */
export const TipIcon = (props: SvgIconProps) => (
  <LightbulbIcon htmlColor={COLOR.amber} fontSize="small" {...props} />
);

/** ⭐ ★ — star (filled). */
export { StarIcon };

/** ☆ — star (outline). */
export { StarBorderIcon };

/** ⬇ — download. */
export { DownloadIcon };

/** ⬆ — upload / escalate. */
export const UploadIcon = ArrowUpwardIcon;

/** ✉ 📧 — email. */
export { EmailIcon, MarkEmailReadIcon };

/** ⚡ — lightning / fast. */
export const LightningIcon = BoltIcon;

/** 🎧 — audio / TTS. */
export { HeadphonesIcon };

/** 🔊 — volume / speaker. */
export { VolumeUpIcon };

/** 🎛 — controls / advanced settings. */
export const ControlsIcon = TuneIcon;

/** ♀ — female. */
export { FemaleIcon };

/** ♂ — male. */
export { MaleIcon };

/** Settings (gear). */
export { SettingsIcon };

/** ⏳ — pending / waiting / hourglass. */
export const HourglassIcon = HourglassEmptyIcon;

// ────────────────────────────────────────────────────────────────────────────
// Platform icons (for ebook export targets)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Map of ebook publishing platform name → icon component. Used by
 * EbookExportPage to render a platform glyph next to each platform name.
 * Where MUI doesn't have a brand-specific icon we use a sensible generic
 * fallback (book / library / bookmark).
 */
export const PLATFORM_ICONS: Record<string, React.ComponentType<SvgIconProps>> = {
  Kindle: MenuBookIcon,
  Kobo: LibraryBooksIcon,
  'Apple Books': MenuBookIcon,
  'Google Play Books': MenuBookIcon,
  'Barnes & Noble': BookmarkIcon,
  'Draft2Digital & Smashwords': DescriptionIcon,
};

/** Default icon for unknown platforms. */
export const DefaultPlatformIcon = DescriptionIcon;

/**
 * Re-export of `AttachMoneyIcon` (used in some currency-specific spots
 * where the more compact `$` style fits better than `MonetizationOnIcon`).
 */
export { AttachMoneyIcon };
